import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, ShieldAlert, History, X, BookOpen, ChevronDown, ChevronRight, FileText, Settings } from 'lucide-react';
import AvaliacaoSAHistorico from './AvaliacaoSAHistorico';
import FormularioVersoes from '../../treinamento/formularios/FormularioVersoes';
import FormularioQuestionario from '../../treinamento/formularios/FormularioQuestionario';
import useEhCompacto from '../../../utils/useEhCompacto';

// Prefixos de API da SA (DEM-00014). O preenchimento/listas usa a base do
// formulário; a gestão de versões tem prefixo próprio — NÃO /api/auditoria/versoes,
// que já é do Checklist (módulo Auditoria).
const API_SA = '/api/auditoria';
const API_VERSOES_SA = '/api/operacional/avaliacaosa/versoes';
// Gate de UI para "Gerenciar versões" (o backend também exige estes perfis).
const PERFIS_GESTAO_VERSOES = ['Admin', 'Operacional', 'Treinamento'];

// =====================================================================
// Sub-hub "Avaliação de Segurança do Alimento" (Módulo Operacional).
//
// DEM-00011 (Fase 4, T22, 2026-09-23): morava no módulo Treinamento (sub-hub
// "Formulários", card "Auditoria SA") — mudou de TELA e VOLTOU A SER
// PREENCHIDA (estava sem escrita desde 09/06/2026 — não por trava técnica,
// por decisão operacional: parou de ser feita pelo portal). Agora é mensal,
// pelos Consultores de Operações (perfil `Operacional`, reusado).
//
// Tabela e rota de API NÃO mudaram (`dtbAuditoriaSA`, `/api/auditoria`) — ver
// CLAUDE.md › migrations e docs/modulos/treinamento.md.
//
// DUAS ABAS (2026-09-23, pedido da Julia, mesmo padrão de
// OperacionalChecklist.js × OperacionalAval360.js): "Avaliação de Segurança
// do Alimento" (método CONTAGEM — onde se preenche) e "Histórico" (método
// PESO — só leitura). Mesma tabela (dtbAuditoriaSA) filtrada por método —
// as duas abas reusam o MESMO AvaliacaoSAHistorico, só com a prop `metodo`
// diferente, em vez de duplicar o componente.
//
// POP-UP "Nova Avaliação" (Fase 7, 2026-09-23, pedido da Julia): a SA passa a
// abrir o mesmo modal visual das outras avaliações (360/Checklist) ao iniciar
// uma nova. ⚠️ A SA NÃO É VERSIONADA (schema fixo) — então onde a 360 lista as
// versões publicadas, aqui há UM card único de início. Versionar a SA de fato
// é card no PLANO (Trilho B). O modal também traz o "Como funciona" com o VETO
// das 7 críticas, que a 360 não tem.
// =====================================================================

const OperacionalAvaliacaoSA = () => {
  // Aba do card: 'atual' (Contagem) | 'historico' (Peso, só leitura).
  const [aba, setAba] = useState('atual');
  const [tela, setTela] = useState('relatorio'); // 'relatorio' | 'preencher' | 'versoes'
  const [modalNovo, setModalNovo] = useState(false); // pop-up "Nova Avaliação"
  const [versaoEscolhida, setVersaoEscolhida] = useState(null); // ID da versão a preencher

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const podeGerenciarVersoes = PERFIS_GESTAO_VERSOES.includes(user.perfil);

  if (tela === 'versoes') {
    return (
      <FormularioVersoes
        apiBase={API_VERSOES_SA}
        cor="#475569"
        titulo="Gerenciar Versões — Segurança do Alimento"
        onVoltar={() => setTela('relatorio')}
      />
    );
  }

  if (tela === 'preencher') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button onClick={() => setTela('relatorio')} style={btnVoltar}>
          <ArrowLeft size={16} /> Voltar
        </button>
        {/* Questionário VERSIONADO (DEM-00014): renderiza a partir da versão
            escolhida no modal. onConcluido volta à lista, que recarrega ao
            remontar — o registro novo aparece no topo. */}
        <FormularioQuestionario
          apiBase={API_SA}
          cor="#475569"
          idVersao={versaoEscolhida}
          titulo="Avaliação de Segurança do Alimento"
          onConcluido={() => setTela('relatorio')}
        />
      </div>
    );
  }

  // acoesExtras: mesmo contrato de Aval360Historico/ChecklistHistorico — o
  // botão de iniciar vive no cabeçalho da consulta, não separado dela. Abre o
  // pop-up (não vai direto ao preenchimento), no padrão das outras avaliações.
  const acoes = (
    <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
      {podeGerenciarVersoes && (
        <button onClick={() => setTela('versoes')} style={btnSecundario} title="Criar/editar versões do formulário">
          <Settings size={16} /> Gerenciar versões
        </button>
      )}
      <button onClick={() => setModalNovo(true)} style={btnPrimario}>
        <Plus size={16} /> Nova Avaliação
      </button>
    </div>
  );

  return (
    <>
      {/* Abas do card: a Avaliação atual (Contagem) e o Histórico (Peso, só leitura). */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid #e2e8f0' }}>
        <button onClick={() => setAba('atual')} style={estiloAba(aba === 'atual')}>
          <ShieldAlert size={15} /> Avaliação de Segurança do Alimento
        </button>
        <button onClick={() => setAba('historico')} style={estiloAba(aba === 'historico')}>
          <History size={15} /> Histórico
        </button>
      </div>

      {aba === 'historico' ? (
        <>
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
            border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 12, padding: '12px 14px', marginBottom: 16,
          }}>
            <History size={18} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13, color: '#92400e' }}>
              <strong>Estas avaliações usaram o método Peso (antes de 2026-09-22).</strong> Este histórico é{' '}
              <strong>somente leitura</strong> — não há classificação por cor para o método antigo (a tabela
              não tem essa coluna); a avaliação atual usa a régua por contagem
              (Verde/Amarelo/Laranja/Vermelho). As avaliações e seus PDFs seguem acessíveis.
            </div>
          </div>
          <AvaliacaoSAHistorico metodo="PESO" titulo="Histórico — método Peso" />
        </>
      ) : (
        <AvaliacaoSAHistorico metodo="CONTAGEM" acoesExtras={acoes} />
      )}

      {modalNovo && (
        <ModalNovaAvaliacaoSA
          onIniciar={(idVersao) => { setModalNovo(false); setVersaoEscolhida(idVersao); setTela('preencher'); }}
          onFechar={() => setModalNovo(false)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------
// Pop-up "Nova Avaliação": no MESMO visual do modal da Avaliação 360
// (ModalNovaAvaliacao em OperacionalAval360.js). ⚠️ A SA NÃO é versionada —
// então onde a 360 lista as versões publicadas, aqui há UM card único de
// início. Traz o "Como funciona" com o método e o VETO das 7 críticas, que a
// 360 não tem. Versionar a SA de fato é card no PLANO (Trilho B).
// ---------------------------------------------------------------------
const ModalNovaAvaliacaoSA = ({ onIniciar, onFechar }) => {
  const compacto = useEhCompacto();
  const [manualAberto, setManualAberto] = useState(false);
  const [versoes, setVersoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // Fecha no Esc — mesma conveniência do modal da 360.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  // Versões PUBLICADAS para escolher (DEM-00014). Substitui o "card único":
  // a SA agora é versionada, como a 360/Checklist.
  useEffect(() => {
    setCarregando(true); setErro('');
    fetch(`${API_SA}/versoes-publicadas`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Erro ao carregar versões.'))))
      .then((data) => setVersoes(Array.isArray(data) ? data : []))
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: compacto ? 10 : 24, overflowY: 'auto',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, marginTop: compacto ? 8 : 24,
          boxShadow: '0 20px 50px rgba(15,23,42,.25)', overflow: 'hidden',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: compacto ? '14px 16px' : '18px 22px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ padding: 10, background: '#e2e8f0', borderRadius: 10 }}><Plus size={20} color="#334155" /></div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Nova Avaliação de Segurança do Alimento</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Inicie a avaliação do mês para a loja.</p>
          </div>
          <button onClick={onFechar} title="Fechar" style={{
            display: 'inline-flex', padding: 8, background: '#f1f5f9', color: '#475569',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: compacto ? 14 : 22 }}>
          {/* "Como funciona" colapsável — mesmo bloco do modal da 360, com o
              método da SA e o VETO das 7 críticas (a 360 não tem veto). */}
          <div style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
            <button onClick={() => setManualAberto((v) => !v)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
              background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 800, color: '#334155', fontSize: 14,
            }}>
              <BookOpen size={18} /> Como funciona / como o resultado é decidido
              <ChevronDown size={18} style={{ marginLeft: 'auto', transform: manualAberto ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>
            {manualAberto && (
              <div style={{ padding: '0 18px 16px', fontSize: 13, color: '#334155', lineHeight: 1.65 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li><strong>Responda cada item</strong> como <strong>Conforme / Não Conforme / N/A</strong>. Marcar <strong>“Não Conforme” exige foto e observação</strong> da evidência.</li>
                  <li style={{ marginTop: 6 }}><strong>Finalize e consulte</strong> — ao Finalizar, o sistema calcula a nota, aplica o veto e gera o <strong>PDF</strong>. A avaliação passa a aparecer na lista, com o resumo por área.</li>
                </ol>
                <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8 }}>
                  <strong style={{ color: '#334155' }}>Como o resultado é decidido</strong><br />
                  Percentual = itens <strong>Conformes</strong> ÷ <strong>aplicáveis</strong> (Conforme + Não Conforme) × 100 — contagem simples. Item <strong>N/A sai do denominador</strong>.<br />
                  Classificação: ≥90% <strong>Verde</strong> · 80–89,99% <strong>Amarelo</strong> · 70–79,99% <strong>Laranja</strong> · &lt;70% <strong>Vermelho</strong>.<br />
                  <span style={{ color: '#991b1b' }}><strong>Veto (STATUS APROVADA/IMPUR):</strong> um único <strong>“Não Conforme”</strong> em qualquer das <strong>7 questões críticas</strong> (FS1, FS2, FS3, FS6, FS7, FS8, FS2026_01) reprova a avaliação (<strong>IMPUR</strong>), por mais alto que seja o percentual; percentual <strong>abaixo de 80%</strong> também reprova.</span>
                </div>
              </div>
            )}
          </div>

          {/* Lista de versões PUBLICADAS (DEM-00014) — como na 360/Checklist.
              Escolher uma leva ao questionário daquela versão. */}
          {carregando && <p style={{ color: '#64748b', margin: 0 }}>Carregando versões…</p>}
          {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>{erro}</div>}
          {!carregando && !erro && versoes.length === 0 && (
            <div style={{ padding: 14, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, fontSize: 14 }}>
              Nenhuma versão publicada ainda. Use <strong>“Gerenciar versões”</strong> para criar e publicar a primeira.
            </div>
          )}
          <div style={{ display: 'grid', gap: 10 }}>
            {versoes.map((v) => (
              <div
                key={v.ID}
                onClick={() => onIniciar(v.ID)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: 18, background: '#fff',
                  border: '1px solid #e2e8f0', borderRadius: 12, cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                <div style={{ padding: 12, background: '#e2e8f0', borderRadius: 10 }}><FileText size={22} color="#334155" /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</div>
                  <div style={{ marginTop: 4, color: '#64748b', fontSize: 14 }}>
                    {v.DESCRICAO ? `${v.DESCRICAO} · ` : ''}{v.TOTAL_PERGUNTAS} perguntas ·{' '}
                    {v.CORTE_VETO === null || v.CORTE_VETO === undefined ? 'sem corte' : `corte ${Number(v.CORTE_VETO)}%`}
                  </div>
                </div>
                <ChevronRight size={20} color="#94a3b8" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Botão de aba do card. A ativa ganha a cor do módulo e uma borda inferior;
// a inativa fica apagada. Mesmo estilo de OperacionalAval360.js/OperacionalChecklist.js.
const estiloAba = (ativa) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
  background: 'transparent', border: 'none', borderBottom: `3px solid ${ativa ? '#475569' : 'transparent'}`,
  color: ativa ? '#0f172a' : '#94a3b8', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginBottom: -1,
});

const btnVoltar = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer', alignSelf: 'flex-start',
};
const btnPrimario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#475569', color: '#fff', border: '1px solid #475569',
  borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};
const btnSecundario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#fff', color: '#475569', border: '1px solid #cbd5e1',
  borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

export default OperacionalAvaliacaoSA;
